import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import {
  areasListSchema,
  createUserResponseSchema,
  createUserSchema,
  okSchema,
  updateUserSchema,
  userSchema,
  usersListSchema,
  type CreateUserInput,
  type UpdateUserInput,
} from './schemas';

const keys = {
  all: ['usuarios'] as const,
  list: () => ['usuarios', 'list'] as const,
};

export function useUsers() {
  return useQuery({
    queryKey: keys.list(),
    queryFn: ({ signal }) => api('/api/users', { schema: usersListSchema, signal }),
    staleTime: 30_000,
  });
}

export function useAreas() {
  return useQuery({
    queryKey: ['areas'] as const,
    queryFn: ({ signal }) => api('/api/areas', { schema: areasListSchema, signal }),
    staleTime: 5 * 60_000, // catálogo muda raramente
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserInput) =>
      api('/api/users', {
        method: 'POST',
        body: createUserSchema.parse(input),
        schema: createUserResponseSchema,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.list() });
    },
  });
}

// useUpdateUser tipo a "User: id como parte do input" para uso em loops/tabelas.
// Para forms ligados a um usuário específico, basta passar o mesmo id em todas as chamadas.
export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateUserInput }) =>
      api(`/api/users/${id}`, {
        method: 'PATCH',
        body: updateUserSchema.parse(patch),
        schema: userSchema,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.list() });
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api(`/api/users/${userId}`, { method: 'DELETE', schema: okSchema }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.list() });
    },
  });
}

export function useResetPasswordAdmin() {
  return useMutation({
    mutationFn: ({ userId, new_password }: { userId: string; new_password: string }) =>
      api(`/api/users/${userId}/password`, {
        method: 'PATCH',
        body: { new_password },
        schema: okSchema,
      }),
  });
}
